import type { BrandConfig } from './types';
import { BRAND_HTML_META } from './htmlMeta';
import { shohinJohinBrand } from './shohin-johin';
import logoWhite from '../../assets/saite_logo_white_trans.png';
import logoColor from '../../assets/saite_logo_color_trans.png';

/**
 * HypeApp OEM ブランド設定
 */
export const hypeappBrand: BrandConfig = {
  ...shohinJohinBrand,
  id: 'hypeapp',
  serviceName: BRAND_HTML_META.hypeapp.serviceName,
  companyName: 'SAITE',
  colorScheme: 'dark',  // 背景は Ink Navy (#05071A) の暗色
  logoWhite: logoWhite,  // ダーク背景向け (白抜き透過ロゴ)
  logoDark: logoColor,   // ライト背景向け (カラー透過ロゴ)
  favicon: BRAND_HTML_META.hypeapp.favicon,
  colors: {
    // primary はソリッド・半透明合成とも統一してシルバー (#686F7D) で運用する。
    // 大面積の主要面 (アコーディオン枠・モーダル枠等) を控えめに見せる狙い。
    //   primary  = シルバー (solid / rgba 合成とも)
    //   sub      = SAITE Blue (個人フォルダタブ等)
    //   accent   = SAITE Blue (主要 CTA)
    primary: '#686F7D',
    primaryHover: '#585E6A',
    primaryRgb: '104 111 125',   // #686F7D (primary と一致)
    subSolid: '#2563FF',
    subSolidHover: '#1d4fd6',
    subRgb: '37 99 255',
    accentSolid: '#2563FF',
    accentSolidHover: '#1d4fd6',
    accentRgb: '37 99 255',
    bgGradientStart: '#05071A',  // Ink Navy (ブランドガイド)
    bgGradientEnd: '#05071A',    // Ink Navy
  },
  urls: {
    website: 'https://saite.co.jp',
    contact: 'https://saite.co.jp/contact.html',
    privacy: 'https://saite.co.jp/policy.html',
    terms: 'https://saite.co.jp/terms.html',
  },
  features: {
    promptVisibility: true,
    helpCenter: true,
    navigationGuide: true,
    omakasePlan: true,
    amazonIntegration: false,
    googleIntegration: true,
    // Canva アプリは全パートナー共有のため「アプリに戻る」に商品上品名義が出るが、
    // HypeApp から連携継続の回答あり (2026-08-21)。停止する場合はここを false に
    // した上で backend/routes/canva.py の CANVA_DISABLED_PARTNER_CODES にも
    // 'hypeapp' を追加する (take8 / usodeki は features を継承しているので、
    // ここを false にする際は両ブランドに明示的な打ち消し true が必要)。
    canvaIntegration: true,
    skuFields: false,
    salesEmail: false,
    gtm: BRAND_HTML_META.hypeapp.gtm,
    oemAttributeManagement: true,
    platformAdmin: false,
    signupEnabled: false,
    mainNavTabs: true,
    appChrome: true,
    publicHome: false,
    materialsNavTab: true, // MainNav に「素材」タブを表示 (表示位置・幅の条件は MainNav 側)
    // SAITE では Fish Audio の「権利処理済み」を既定オンにし、オフへの切り替え時に注意喚起を出す
    // (take8 / usodeki は features を継承しているので、それぞれ明示的に false で打ち消している)
    fishLicensedVoicesDefault: true,
    // 動画のプロンプト入力モードの 3 タブ目「モーショングラフィックス」(段階 3 の完了 = 一般公開)。
    // true でも backend の暫定ゲート (GET /api/motion-layered/availability の reason='restricted') の間は出ない。
    // レンダラーの状態では消さない (生成の受付で確かめる)。料金行未適用はタブを出したまま生成ボタンを無効にする
    // (take8 / usodeki は features を継承)
    motionGraphicsPromptMode: true,
  },
  labels: {
    ...shohinJohinBrand.labels,
    studio: '制作スタジオ',
    studioShort: 'スタジオ',
    material: '素材',
    materials: '素材',
    product: 'プロジェクト',
    products: 'プロジェクト',
    aiActor: 'AIモデル',
    aiActors: 'AIモデル',
    attributeMode: '構成アシストモード',
    scenarioMode: '絵コンテ作成モード',
    scenarioModeBase: '絵コンテ作成',
    scenarioNoun: '絵コンテ',
    scenarioSettings: '絵コンテ作成設定',
    scenarioGenerate: '絵コンテを生成',
    scenarioRegenerate: '絵コンテを再生成',
    scenarioEdit: '絵コンテ編集',
    productName: 'プロジェクト名',
    productDescription: 'プロジェクトの説明',
    productNewDefault: 'プロジェクト情報を入力してください',
    productShort: 'プロジェクト',
  },
  exportSettings: {
    resolutions: ['720p', '1080p', '2K'],
    formats: {
      video: ['mp4'],
      image: ['jpg', 'png'],
    },
  },
  platforms: ['Google', 'Yahoo', 'Meta', 'TikTok', 'X'],
  // ※ HypeApp 社からのたたき受領前のプレースホルダ文言・導線。計画書
  //   docs/requirements/login-auto-provision-gating-plan.md §4.6 / §9 未決事項。
  //   最終文言・デザインが届き次第差し替える。
  auth: {
    notFoundGuidance: {
      type: 'external',
      url: 'https://saite.co.jp/demo.html',
      messagePrefix: 'アカウントが見つかりませんでした。まずは',
      linkLabel: 'デモ体験',
      messageSuffix: 'からご登録ください。',
    },
    // HypeApp 依頼2: ログイン画面をサービスサイト階層に組み込むパンくず
    // (アカウント開設ページは noindex 想定のため出さない)。
    // ラベル・デザインは saite.co.jp の LP のパンくず (「ホーム › 現在地」) に合わせる
    loginBreadcrumb: [
      { label: 'ホーム', url: 'https://saite.co.jp/' },
      { label: 'ログイン' },
    ],
    // HypeApp 依頼3: ロゴクリックでサービスサイトトップへ
    logoHref: 'https://saite.co.jp/',
  },
  // 初回アンケート: 利用目的 → きっかけ (いずれも複数選択・DB 駆動)。
  // backend の SURVEY_CONFIGS['hypeapp'] と揃えること。
  surveyQuestions: ['usage_purpose', 'discovery_channel'],
  homePageVariant: 'hypeapp',
  homePage: {
    promptPlaceholder: '例：このペットボトルを、公園で20代の日本人女性が飲んでいる',
  },
};
